"use client";

import { createContext, useCallback, useContext, useMemo, useState } from "react";

import { useOpenWithFocus } from "./use-open-with-focus";

type BookingContextValue = {
  open: boolean;
  // Where the drawer was opened from (e.g. "dock", "desktop-nav", or a
  // section heading) — recorded for analytics by the trigger.
  source: string | null;
  setOpen: (open: boolean, source?: string) => void;
};

const BookingContext = createContext<BookingContextValue | null>(null);

export function BookingProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpenInner] = useOpenWithFocus(false);
  const [source, setSource] = useState<string | null>(null);

  const setOpen = useCallback(
    (next: boolean, src?: string) => {
      if (next) setSource(src ?? null);
      setOpenInner(next);
    },
    [setOpenInner],
  );

  const value = useMemo(
    () => ({ open, source, setOpen }),
    [open, source, setOpen],
  );
  return (
    <BookingContext.Provider value={value}>{children}</BookingContext.Provider>
  );
}

export function useBookingDrawer() {
  const ctx = useContext(BookingContext);
  if (!ctx)
    throw new Error("useBookingDrawer must be used inside BookingProvider");
  return ctx;
}
